import * as yup from 'yup';
import {useTranslation} from 'react-i18next';
import {Modal} from 'react-bootstrap';
import {Formik} from 'formik';
import Select from 'react-select';
import {useDispatch} from 'react-redux';
import {showLoader, hideLoader} from '../../store/common/slice';
import {addVendor} from '../../store/expenses/actions';
import InputGroup from '../../common/InputGroup';
import {NewSupplierPopUpStyled} from './expenses.styled';
import Loader from '../../common/simple/loader';
import {BllinkLogger} from '../../common/utils/bllink_loggers';
import Button from '../../common/Button';
import {newSelectStyles} from '../../common/shared/select.styled';
import Checkbox from '../../common/simple/Checkbox';
import check from '../../images/newDesign/check.svg';

export default function NewSupplierPopUp({show, onModalHide, buildingID, onVendorAdded, bankOptions}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const validationSchema = yup.object().shape({
        name: yup.string().required(t('titles.forms.mandatory_field')),
        email: yup.string().email(t('titles.forms.correct_email')),
        phoneNumber: yup.string().matches(/^[0-9]+$/, t('titles.forms.correct_phone_for_vendor')).min(3).max(10),
        comment: yup.string().max(100),
        vendorTaxID: yup.string().min(6, t('titles.forms.vendor_tax_id')),
        branchNum: yup.string().matches(/^\d+$/, t('titles.forms.number_field')),
        accountNum: yup.string(),
        bankCode: yup.object(),
    });
    const initialFormValues = {
        name: '',
        email: '',
        phoneNumber: '',
        vendorTaxID: '',
        comment: '',
        bankCode: '',
        branchNum: '',
        accountNum: '',
        chargeCommissionFrom: 'vendor'
    };

    const submitForm = async (evt, values) => {
        evt.preventDefault();
        dispatch(showLoader());
        const dataPayload = {
            name: values.name,
            firstName: values.name,
            lastName: values.name,
            ownerBuildingID: buildingID,
            ...(values.comment && {comment: values.comment}),
            ...(values.phoneNumber && {phone: values.phoneNumber}),
            ...(values.vendorTaxID && {taxID: values.vendorTaxID}),
        }
        BllinkLogger.info(`payload for submitting`, dataPayload);

        const {payload} = await dispatch(addVendor(dataPayload));
        dispatch(hideLoader());

        if (payload && payload.externalId) {
            alert(t('expenses.vendor_created'));
            onVendorAdded();
        }
    }

    return (
        <NewSupplierPopUpStyled
            className={'header-with-bg'}
            dialogClassName={'supplier-modal'}
            show={show}
            onHide={onModalHide}
            centered
        >
            <span
                className={'modal-close'}
                onClick={onModalHide}
            >
                &#215;
            </span>
            <Modal.Header>
                <Modal.Title className={'text-center'}>
                    {t('expenses.new_supplier')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader/>
                <Formik
                    initialValues={initialFormValues}
                    validationSchema={validationSchema}
                >
                    {({
                          errors,
                          touched,
                          values,
                          handleChange,
                          handleBlur,
                          isValid,
                          setFieldTouched,
                          setFieldValue,
                          dirty,
                      }) => (
                        <form name="js-new-vendor">
                            <div className='data-block'>
                                <h3>{t('expenses.vendorDetails')}</h3>
                                <div className='modal-row'>
                                    <InputGroup
                                        labelText={t('expenses.name')}
                                        labelFor={'name'}
                                        errorText={errors.name && touched.name ? errors.name : ''}
                                        withBorder
                                    >
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name && touched.name ? 'error' : ''}`}
                                            name="name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.name}
                                        />
                                    </InputGroup>

                                    <InputGroup
                                        labelText={t('expenses.email')}
                                        labelFor={'email'}
                                        errorText={errors.email && touched.email ? errors.email : ''}
                                        withBorder
                                    >
                                        <input
                                            type="text"
                                            className={`form-control ${errors.email && touched.email ? 'error' : ''}`}
                                            name="email"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.email}
                                        />
                                    </InputGroup>

                                    <InputGroup
                                        labelText={t('expenses.phone_number')}
                                        labelFor={'phoneNumber'}
                                        errorText={errors.phoneNumber && touched.phoneNumber ? errors.phoneNumber : ''}
                                        withBorder
                                    >
                                        <input
                                            type="text"
                                            className={`form-control ${errors.phoneNumber && touched.phoneNumber ? 'error' : ''}`}
                                            name="phoneNumber"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.phoneNumber}
                                        />
                                    </InputGroup>
                                </div>
                                <div className='modal-row'>
                                    <InputGroup
                                        classes='comment'
                                        labelText={t('expenses.comment')}
                                        labelFor={'comment'}
                                        errorText={errors.comment && touched.comment ? errors.comment : ''}
                                        withBorder
                                    >
                                        <input
                                            type="text"
                                            className={`form-control ${errors.comment && touched.comment ? 'error' : ''}`}
                                            name="comment"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.comment}
                                        />
                                    </InputGroup>

                                    <InputGroup
                                        labelText={t('expenses.vat_number')}
                                        labelFor={'vendorTaxID'}
                                        errorText={errors.vendorTaxID && touched.vendorTaxID ? errors.vendorTaxID : ''}
                                        withBorder
                                    >
                                        <input
                                            type="text"
                                            className={`form-control ${errors.vendorTaxID && touched.vendorTaxID ? 'error' : ''}`}
                                            name="vendorTaxID"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.vendorTaxID}
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                            <div className='data-block'>
                                <h3>{t('expenses.bankDetails')}</h3>
                                <div className='modal-row'>
                                    <InputGroup
                                        classes={'bank'}
                                        labelText={t('offline_payments.bank_code_title')}
                                        errorText={errors.bankCode && touched.bankCode ? errors.bankCode : ''}
                                        withBorder
                                    >
                                        <Select
                                            styles={newSelectStyles}
                                            options={bankOptions}
                                            onChange={type => setFieldValue('bankCode', type)}
                                            onBlur={() => setFieldTouched('bankCode', true)}
                                            value={values.bankCode || ''}
                                        />
                                    </InputGroup>

                                    <InputGroup
                                        labelText={t('offline_payments.bank_branch_title')}
                                        errorText={errors.branchNum && touched.branchNum ? errors.branchNum : ''}
                                        withBorder
                                    >
                                        <input
                                            type="text"
                                            className={`form-control  ${errors.branchNum && touched.branchNum ? 'error' : ''}`}
                                            name={`branch`}
                                            value={values.branchNum || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </InputGroup>

                                    <InputGroup
                                        labelText={t('offline_payments.bank_account_number_title')}
                                        errorText={errors.accountNum && touched.accountNum ? errors.accountNum : ''}
                                        withBorder
                                    >
                                        <input
                                            type="text"
                                            className={`form-control  ${errors.accountNum && touched.accountNum ? 'error' : ''}`}
                                            name={`accountNum`}
                                            value={values.accountNum || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </InputGroup>
                                </div>
                            </div>

                            <div className='data-block'>
                                <h3>{t('expenses.commission')}</h3>
                                <div className='modal-row'>
                                    <Checkbox
                                        label={t('expenses.vendorCommissionLabel')}
                                        checked={values.chargeCommissionFrom === 'vendor'}
                                        onChange={value => setFieldValue('chargeCommissionFrom', value ? 'vendor' : '')}
                                        name={'vendor'}
                                        icon={<img src={check} alt="check" />}
                                        borderColor="#7D97FF"
                                        borderWidth={1}
                                        borderRadius={4}
                                        style={{ overflow: "hidden" }}
                                        size={16}
                                    />

                                    <Checkbox
                                        label={t('expenses.bankCommissionLabel')}
                                        checked={values.chargeCommissionFrom === 'bank'}
                                        onChange={value => setFieldValue('chargeCommissionFrom', value ? 'bank' : '')}
                                        name={'bank'}
                                        icon={<img src={check} alt="check" />}
                                        borderColor="#7D97FF"
                                        borderWidth={1}
                                        borderRadius={4}
                                        style={{ overflow: "hidden" }}
                                        size={16}
                                    />
                                </div>
                            </div>

                            <div className='modal-row'>
                                <Button
                                    type={'button'}
                                    rounded={false}
                                    disabled={!(isValid && dirty)}
                                    onClick={(evt) => submitForm(evt, values)}
                                >
                                    {t('expenses.add_vendor')}
                                </Button>
                            </div>
                        </form>
                    )}
                </Formik>
            </Modal.Body>
        </NewSupplierPopUpStyled>
    );
}


